<!-- eslint-disable vue/custom-event-name-casing -->
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import './vue-img/styles.scss'
import { getLazySrc } from '@ui/composables'
import { Image } from '@ui/base-components/skeleton'
import { stripHtml } from '@general-function'
import handleImage from './vue-img/function'

interface Props {
  src: string
  id: string
  classLoading?: string
  handleSrc?: string
  class?: string
  disabled?: boolean
  loading?: boolean
  alt: string
  width?: number
  height?: number
  maxWidth?: number
  maxHeight?: number
  classes?: string
  mouseover?: Function
  style?: Record<string, string>
  placeholder?: string
  isLazyLoad?: boolean // menambahkan prop untuk blur placeholder
}

const props = withDefaults(defineProps<Props>(), {
  src: '',
  classLoading: '',
  handleSrc: '',
  class: '',
  id: '',
  disabled: false,
  loading: false,
  alt: '',
  width: 600,
  height: 0,
  maxWidth: undefined,
  maxHeight: undefined,
  classes: '',
  style: () => ({}),
  placeholder: 'Coming+Soon',
})

const emit = defineEmits<{
  (e: ':click', event: MouseEvent): void
  (e: 'mouseover', event: MouseEvent): void
}>()

const isImageLoaded = ref(false)
const isMirage = computed(() => (document && document.scripts && [...document.scripts].some(script =>
  script.src.includes('cloudflare-static/mirage2.min.js'))) || (window && typeof window.Mirage !== 'undefined'))

onMounted(() => {
  handleImage()
  handleLazyLoadImg()
})
function handleClick(e: MouseEvent) {
  emit(':click', e)
}
// const { url: placeholder } = getLazySrc(props.src, true)

// Update currentImage computation
const currentImage = computed(() =>
  isImageLoaded.value ? props.src : getLazySrc(props.src),
)
const blurImage = computed(() => getLazySrc(props.src))
// function handleMouseOver(e) {
//   if (props?.mouseover)
//     props?.mouseover(e)
// }
function handleLazyLoadImg() {
  if (typeof window === 'undefined' || typeof document === 'undefined')
    return

  const lazyImages = Array.from(document.querySelectorAll('img.lazy'))

  if ('IntersectionObserver' in window) {
    const lazyImageObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const lazyImage = entry.target as HTMLImageElement
          if (lazyImage.dataset.src) {
            lazyImage.src = lazyImage.dataset.src
            lazyImage.srcset = lazyImage.dataset.srcset || ''
            lazyImage.classList.remove('lazy')
            lazyImageObserver.unobserve(lazyImage)
          }
        }
      })
    })

    lazyImages.forEach((lazyImage) => {
      lazyImageObserver.observe(lazyImage)
    })
  }
  else {
    // Fallback for browsers that don't support IntersectionObserver
    // Implement a simple lazy loading here if needed
  }
}
</script>

<template>
  <!-- Blur placeholder -->
  <img
    v-if="isLazyLoad !== undefined"
    :src="blurImage"
    :data-src="blurImage"
    :class="`blur-placeholder transition-opacity delay-150 duration-200 transition-discrete ${isLazyLoad && !isImageLoaded ? '' : 'opacity-0 !w-0 !h-0 hidden'}`"
    :alt="stripHtml(props.alt, false)"
  />
  <img
    :id="props?.id"
    :style="props.style"
    :class="`${props.classes} ${props.class} v-img_load lazy`"
    :height="props.height"
    :width="props.width"
    :src="isMirage ? undefined : currentImage"
    :data-cfsrc="isMirage ? currentImage : undefined"
    :data-src="currentImage"
    :alt="stripHtml(props.alt, false)"
    :max-width="props.maxWidth"
    :loading="isMirage ? undefined : 'lazy'"
    :decoding="isMirage ? undefined : 'async'"
    :max-height="props.maxHeight"
    @mouseover="(e: MouseEvent) => emit('mouseover', e)"
    @error="() => isImageLoaded = true"
    @load="() => isImageLoaded = true"
    @click="handleClick"
  />
  <Image
    v-if="(!isImageLoaded && props.loading) || props.disabled"
    class="vue-img-skeleton"
    :classes="`${props.classLoading} h-full w-full bg-gray-300 !dark:bg-gray-700`"
  />
</template>

<style lang="scss" scoped>
.vue-img-skeleton {
  position: absolute;
  z-index: 1;
  inset: 0;
  width: 100%;
  height: 100%;
}
.blur-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  object-fit: cover;
  height: 100%;
  filter: blur(10px);
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.v-img_load {
  position: relative;
  z-index: 1;

  &.loaded {
    & + .blur-placeholder {
      opacity: 0;
    }
  }
}
</style>
